import api from './api';

const BASE_URL = '/personal/teams';

/**
 * Personal Teams Service
 * Handles all API calls for personal workspace teams (non-organization)
 */

/**
 * Get all personal teams for authenticated user
 * @param {Object} params - Query parameters (search, status, page, limit)
 * @returns {Promise} Teams list with pagination
 */
export const getPersonalTeams = async (params = {}) => {
  const response = await api.get(BASE_URL, { params });
  return response.data;
};

/**
 * Create a new personal team
 * @param {Object} teamData - Team data (name, description, color, members)
 * @returns {Promise} Created team
 */
export const createPersonalTeam = async (teamData) => {
  const response = await api.post(BASE_URL, teamData);
  return response.data;
};

/**
 * Get personal team by ID
 * @param {string} teamId - Team ID
 * @returns {Promise} Team with members
 */
export const getPersonalTeamById = async (teamId) => {
  const response = await api.get(`${BASE_URL}/${teamId}`);
  return response.data;
};

/**
 * Update personal team
 * @param {string} teamId - Team ID
 * @param {Object} updateData - Data to update (name, description, color, settings)
 * @returns {Promise} Updated team
 */
export const updatePersonalTeam = async (teamId, updateData) => {
  const response = await api.put(`${BASE_URL}/${teamId}`, updateData);
  return response.data;
};

/**
 * Delete personal team
 * @param {string} teamId - Team ID
 * @returns {Promise} Success message
 */
export const deletePersonalTeam = async (teamId) => {
  const response = await api.delete(`${BASE_URL}/${teamId}`);
  return response.data;
};

/**
 * Get team statistics
 * @param {string} teamId - Team ID
 * @returns {Promise} Team stats
 */
export const getPersonalTeamStats = async (teamId) => {
  const response = await api.get(`${BASE_URL}/${teamId}/stats`);
  return response.data;
};

/**
 * Invite member to team (sends invitation email)
 * @param {string} teamId - Team ID
 * @param {Object} memberData - Member data (email, role, message)
 * @returns {Promise} Invitation details
 */
export const addMemberToTeam = async (teamId, memberData) => {
  const response = await api.post(`${BASE_URL}/${teamId}/members`, memberData);
  return response.data;
};

/**
 * Update team member
 * @param {string} teamId - Team ID
 * @param {string} memberId - Member ID
 * @param {Object} updateData - Data to update (role, permissions)
 * @returns {Promise} Updated member
 */
export const updateTeamMember = async (teamId, memberId, updateData) => {
  const response = await api.put(`${BASE_URL}/${teamId}/members/${memberId}`, updateData);
  return response.data;
};

/**
 * Remove member from team
 * @param {string} teamId - Team ID
 * @param {string} memberId - Member ID
 * @returns {Promise} Success message
 */
export const removeTeamMember = async (teamId, memberId) => {
  const response = await api.delete(`${BASE_URL}/${teamId}/members/${memberId}`);
  return response.data;
};

/**
 * Leave team
 * @param {string} teamId - Team ID
 * @returns {Promise} Success message
 */
export const leaveTeam = async (teamId) => {
  const response = await api.post(`${BASE_URL}/${teamId}/leave`);
  return response.data;
};

// ==================== INVITATION MANAGEMENT ====================

/**
 * Get team invitations
 * @param {string} teamId - Team ID
 * @param {string} status - Filter by status (pending, accepted, expired, declined)
 * @returns {Promise} Invitations list with counts
 */
export const getTeamInvitations = async (teamId, status = null) => {
  const params = status ? { status } : {};
  const response = await api.get(`${BASE_URL}/${teamId}/invitations`, { params });
  return response.data;
};

/**
 * Resend team invitation
 * @param {string} teamId - Team ID
 * @param {string} invitationId - Invitation ID
 * @returns {Promise} Updated invitation
 */
export const resendInvitation = async (teamId, invitationId) => {
  const response = await api.post(`${BASE_URL}/${teamId}/invitations/${invitationId}/resend`);
  return response.data;
};

/**
 * Cancel team invitation
 * @param {string} teamId - Team ID
 * @param {string} invitationId - Invitation ID
 * @returns {Promise} Success message
 */
export const cancelInvitation = async (teamId, invitationId) => {
  const response = await api.delete(`${BASE_URL}/${teamId}/invitations/${invitationId}`);
  return response.data;
};

/**
 * Get invitation details by token (for accept page)
 * @param {string} token - Invitation token
 * @returns {Promise} Invitation details
 */
export const getInvitationByToken = async (token) => {
  const response = await api.get(`${BASE_URL}/invite/${token}`);
  return response.data;
};

/**
 * Accept team invitation
 * @param {string} token - Invitation token
 * @returns {Promise} Team data after joining
 */
export const acceptInvitation = async (token) => {
  const response = await api.post(`${BASE_URL}/invite/accept/${token}`);
  return response.data;
};

/**
 * Decline team invitation
 * @param {string} token - Invitation token
 * @returns {Promise} Success message
 */
export const declineInvitation = async (token) => {
  const response = await api.post(`${BASE_URL}/invite/decline/${token}`);
  return response.data;
};

/**
 * Get my pending team invitations
 * @returns {Promise} List of pending invitations
 */
export const getMyPendingInvitations = async () => {
  const response = await api.get(`${BASE_URL}/invitations/my`);
  return response.data;
};

export default {
  getPersonalTeams,
  createPersonalTeam,
  getPersonalTeamById,
  updatePersonalTeam,
  deletePersonalTeam,
  getPersonalTeamStats,
  addMemberToTeam,
  updateTeamMember,
  removeTeamMember,
  leaveTeam,
  // Invitation methods
  getTeamInvitations,
  resendInvitation,
  cancelInvitation,
  getInvitationByToken,
  acceptInvitation,
  declineInvitation,
  getMyPendingInvitations
};
